import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import Row from '../components/Row'
import { styles } from '../styles/themes/style'
import SvgFragment from './SvgFragment'
import Button from '../components/Button'
import { useAppHandlers } from '../services/actions/app/useAppHandlers'
import { useAuthStore } from '../stores/auth/useAuthStore'
import Text from '../components/Text'
import useMediaQuery from '../components/useMediaQuery'
import Column from '../components/Column'
import { formatNumber } from '../utils';
import { useState, useRef } from 'react';
import { useExternalHandlers } from '../services/actions/navigation/useExternalHandlers'

interface IGetMoreCreditsProps {}

const GetMoreCredits: FunctionComponent<IGetMoreCreditsProps> = () => {
    const { setCalculateEnrichmentModalHandler } = useAppHandlers();
    const { learnMoreAboutCredits } = useExternalHandlers();
    const { store: { workspace }} = useAuthStore()

    const isMobile = useMediaQuery("(max-width: 768px)");
    const minEnrichmentCredits = 30

    const [isHovered, setIsHovered] = useState(false);
    const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

    function handleMouseEnter() {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
            hoverTimeout.current = null;
        }
        setIsHovered(true);
    }

    function handleMouseLeave() {
        hoverTimeout.current = setTimeout(() => {
            setIsHovered(false);
        }, 120);
    }

    return (
        <Row>
            {!isMobile && (
                <Credits
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <StyledRow alignItems gap='5px'>
                        <CoinsWrapper>
                            <SvgFragment type='coins'/>
                            {workspace.enrichmentCredits < 15 && <RedDot />}
                        </CoinsWrapper>
                        <Text>Credits</Text>
                        <Text $bold>{formatNumber(workspace.enrichmentCredits)}</Text>
                    </StyledRow>
                    {isHovered && (
                        <PopupWindow gap="10px">
                            <PopupSection as={Row} spaceBetween>
                                <Text $bold>Credits left</Text>
                                <Text>{formatNumber(workspace.enrichmentCredits)}</Text>
                            </PopupSection>
                            <PopupSection as={Column} gap='5px' $backgroundColor="highlight">
                                {workspace.enrichmentCredits <= 0 && <Text $paragraph3>You have no credits left. Get more credits to continue enrichment.</Text>}
                                {workspace.enrichmentCredits > 0 && workspace.enrichmentCredits < 15 && <Text $paragraph3>You’re on a roll. Get more credits and keep up the momentum.</Text>}
                                <UnderlineActionText onClick={learnMoreAboutCredits}>Learn more about credits.</UnderlineActionText>
                            </PopupSection>
                            <CreditsInfo gap='8px'>
                                <Text $bold $lighter>Credit usage:</Text>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                    {[{value: 'Per found Business email', credits: '1 credit'}, {value: 'Per found Personal email', credits: '3 credits'}, {value: 'Per found Phone number', credits: '10 credits'}].map((item, key) => (
                                        <Row key={key} spaceBetween>
                                            <Text>{item.value}</Text>
                                            <Text>{item.credits}</Text>
                                        </Row>
                                    ))}
                                </div>
                            </CreditsInfo>
                        </PopupWindow>
                    )}
                </Credits>
            )}
            <Button tooltipMessage='' $type='yellow600' onClickHandler={() => setCalculateEnrichmentModalHandler(() => {}, () => {}, false, [], [], false, false, false, true)}>Get more credits</Button>
        </Row>
    )
}

const PopupWindow = styled(Column)`
    position: absolute;
    top: 40px;
    right: 0;
    z-index: 999;
    width: 316px;
    background-color: ${styles.colors.white};
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    padding: 12px;
`
const PopupSection = styled.section<{ $backgroundColor?: string }>`
    background-color: ${({ $backgroundColor }) =>
        $backgroundColor === 'highlight' ? styles.colors.yellow300 : styles.colors.black100
    };
    border-radius: 4px;
    padding: 8px;
`

const StyledRow = styled(Row)`
    position: relative;
    height: 34px;
    background-color: ${styles.colors.white};
    padding: 5px 10px 5px 5px;
    border-radius: 4px 0px 0px 4px;
    cursor: pointer;
`
const CoinsWrapper = styled.div`
    position: relative;
    margin-right: 3px;
`
const RedDot = styled.div`
    position: absolute;
    top: -2px;
    right: -4px;
    width: 8px;
    height: 8px;
    background-color: ${styles.colors.red600};
    border-radius: 50%;
`
const CreditsInfo = styled(Column)`
    margin-top: 5px;
`
const Credits = styled.div`
    position: relative;
    display: inline-block;
`
const UnderlineActionText = styled.span`
    cursor: pointer;
    font-family: NunitoSans;
    font-weight: 400;
    font-style: Regular;
    font-size: 12px;
    leading-trim: NONE;
    line-height: 18px;
    letter-spacing: 0px;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-offset: 0%;
    text-decoration-thickness: 0%;
    color: ${styles.colors.black600};

    &:hover {
        color: ${styles.colors.primary600};
    }
`
export default GetMoreCredits
